import React, { useMemo } from 'react';
import { Box, Typography, Tooltip as MuiTooltip } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { AgingResponse, AggregatorSettlement } from '../../api/aggregatorApi';
import CommissionBurnChart from './CommissionBurnChart';
import { SecondaryButton } from '../ui';

export type AggregatorAgingBucketLabel = '0-7d' | '7-14d' | '14-30d' | '30+d';

const AGING_ORDER: AggregatorAgingBucketLabel[] = ['0-7d', '7-14d', '14-30d', '30+d'];

interface Props {
  aging: AgingResponse | null;
  settlements: AggregatorSettlement[];
  dateFrom: string;
  dateTo: string;
  selectedAgingBucket: string | null;
  onAgingClick: (label: string | null) => void;
  onExportAging: () => void;
  onSettlementClick: (id: number) => void;
}

const AggregatorCharts: React.FC<Props> = ({
  aging,
  settlements,
  dateFrom,
  dateTo,
  selectedAgingBucket,
  onAgingClick,
  onExportAging,
  onSettlementClick,
}) => {
  const { theme } = useTheme();

  const agingColor = (b: string): string => {
    if (b === '0-7d') return theme.success || '#5B8C7E';
    if (b === '7-14d') return theme.warning || '#C9A961';
    if (b === '14-30d') return '#E08E5C';
    return theme.danger || '#C97B63';
  };

  const agingData = useMemo(() => {
    if (!aging) return { totals: {} as Record<string, number>, counts: {} as Record<string, number>, total: 0 };
    const totals: Record<string, number> = {};
    const counts: Record<string, number> = {};
    let total = 0;
    aging.buckets.forEach((b) => {
      totals[b.label] = Number(b.total || 0);
      counts[b.label] = b.count || 0;
      total += totals[b.label];
    });
    return { totals, counts, total };
  }, [aging]);

  const topDrift = useMemo(() => {
    return [...settlements]
      .filter((s) => Math.abs((s.commissionDrift || 0)) > 0 || Math.abs((s.unexplainedVariance || 0)) > 0)
      .sort(
        (a, b) =>
          Math.abs((b.commissionDrift || 0)) + Math.abs((b.unexplainedVariance || 0)) -
          (Math.abs((a.commissionDrift || 0)) + Math.abs((a.unexplainedVariance || 0))),
      )
      .slice(0, 3);
  }, [settlements]);

  const cardSx = {
    backgroundColor: theme.components?.card?.background || '#FFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
    borderRadius: '12px',
    p: { xs: 1.5, sm: 2 },
  };

  const sectionLabel = {
    color: theme.gray?.[500] || '#666',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.65rem',
    letterSpacing: '0.05em',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* Card 1 — Aging stacked bar */}
      <Box sx={cardSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            mb: 1,
          }}
        >
          <Typography variant="caption" sx={sectionLabel}>
            Aging — pending entries · tap segment to filter
          </Typography>
          <SecondaryButton onClick={onExportAging} sx={{ fontSize: '0.75rem', py: 0.5 }}>
            Export Chase List
          </SecondaryButton>
        </Box>

        {agingData.total === 0 ? (
          <Typography sx={{ color: theme.gray?.[400], fontSize: '0.8rem', textAlign: 'center', py: 1.5 }}>
            No pending entries
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                mt: 0.5,
                height: 28,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: `inset 0 0 0 1px ${theme.gray?.[200] || '#E5E5E5'}`,
              }}
            >
              {AGING_ORDER.map((bucket) => {
                const value = agingData.totals[bucket] || 0;
                if (value <= 0) return null;
                const pct = (value / agingData.total) * 100;
                const selected = selectedAgingBucket === bucket;
                return (
                  <MuiTooltip
                    key={bucket}
                    title={`${bucket}: ${formatCurrency(value, { showDecimals: false })} (${agingData.counts[bucket]} entries · ${pct.toFixed(1)}%)`}
                    arrow
                    placement="top"
                  >
                    <Box
                      onClick={() => onAgingClick(selected ? null : bucket)}
                      role="button"
                      aria-label={`Filter by ${bucket}`}
                      data-testid={`agg-aging-segment-${bucket}`}
                      sx={{
                        flex: pct,
                        backgroundColor: agingColor(bucket),
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-inverse)',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        opacity: selectedAgingBucket && !selected ? 0.4 : 1,
                        transition: 'opacity 0.15s',
                        '&:hover': { opacity: selected ? 1 : 0.85 },
                      }}
                    >
                      {pct >= 10 ? bucket : ''}
                    </Box>
                  </MuiTooltip>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
              {AGING_ORDER.map((bucket) => (
                <Box key={bucket} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: agingColor(bucket) }} />
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                    {bucket}: {formatCurrency(agingData.totals[bucket] || 0, { showDecimals: false })} ({agingData.counts[bucket] || 0})
                  </Typography>
                </Box>
              ))}
            </Box>
            {selectedAgingBucket && (
              <Typography
                component="button"
                type="button"
                onClick={() => onAgingClick(null)}
                sx={{
                  mt: 1,
                  fontSize: '0.7rem',
                  color: theme.primary,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 0,
                  p: 0,
                  minHeight: 44,
                  textAlign: 'left',
                  display: 'inline-flex',
                  alignItems: 'center',
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                Clear age filter ({selectedAgingBucket})
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Card 2 — Commission burn + top drift */}
      <Box sx={cardSx}>
        <Typography variant="caption" sx={sectionLabel}>
          Commission burn · effective rate over settlements
        </Typography>
        <Box sx={{ mt: 0.5, mx: { xs: -0.5, sm: 0 } }}>
          <CommissionBurnChart dateFrom={dateFrom} dateTo={dateTo} />
        </Box>

        {topDrift.length > 0 && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.gray?.[200] || '#E5E5E5'}` }}>
            <Typography variant="caption" sx={sectionLabel}>
              Biggest drift settlements · tap to review
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              {topDrift.map((s) => {
                const drift = s.commissionDrift || 0;
                const variance = s.unexplainedVariance || 0;
                const driftColor = drift < 0 ? theme.danger : theme.warning;
                return (
                  <Box
                    key={s.id}
                    onClick={() => onSettlementClick(s.id)}
                    role="button"
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 0.25, sm: 1 },
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: theme.gray?.[100] || '#F5F5F5',
                      '&:hover': { backgroundColor: `${theme.primary}18` },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.text }}>
                      {s.channelName} · {s.paymentDate}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: driftColor || theme.gray?.[600] }}>
                        Drift: {formatCurrency(drift, { showDecimals: false })}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: Math.abs(variance) > 0 ? theme.danger : theme.gray?.[600] }}>
                        Variance: {formatCurrency(variance, { showDecimals: false })}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AggregatorCharts;
